import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { readAuthoredItem } from "../../../api/content/authoring/processAuthoringApi";
import type { WorkflowGraphDensity } from "../../../api/userPreferencesApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useLoad } from "../../../shared/loading/useLoad";
import LoadingIndicator from "../../../ui/feedback/LoadingIndicator";
import { useAuthoringOptions } from "../../processAuthoring/draft/authoringOptions";
import type { LoadedProcess } from "../data/processCatalogue";
import WorkflowEditor from "./WorkflowEditor";

/** Reads what edit mode starts from: the Workflow's own definition, and the forms' options.
 * `density`: the view's own, resolved by the embedding (F76): the Workflow page's, or a person
 * step's default. `onClose` leaves edit mode; an embedding without one, such as a person step,
 * has no Close. `carry`: a step or sub-workflow version to add as it opens ("Use in a Workflow"). */
export default function WorkflowEditMode({ projectId, reference, process, density, onClose, carry = null }: { projectId: string; reference: string; process: LoadedProcess; density: WorkflowGraphDensity; onClose?: () => void; carry?: string | null }) {
  const { t } = useTranslation();
  const load = useCallback((signal: AbortSignal) => readAuthoredItem(reference, signal), [reference]);
  const item = useLoad(load);
  const options = useAuthoringOptions(projectId);
  if (item.state.status === "loading" || options.state.status === "loading") {
    return <LoadingIndicator label={t("workflows.edit.loading")} className="py-10" />;
  }
  if (item.state.status === "failed") {
    return <ErrorNotice error={item.state.error} onRetry={item.reload} />;
  }
  if (options.state.status === "failed") {
    return <ErrorNotice error={options.state.error} onRetry={options.reload} />;
  }
  const content = item.state.value.content;
  if (content?.kind !== "workflow") {
    return <ErrorNotice error={{ kind: "invalidAddress", address: reference }} />;
  }
  return (
    <WorkflowEditor
      projectId={projectId}
      base={{ reference: item.state.value.reference, itemId: item.state.value.itemId, content: content.definition }}
      process={process}
      options={options.state.value}
      density={density}
      onClose={onClose}
      carry={carry}
    />
  );
}
